import SEOHead from '@/components/feature/SEOHead';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import { testimonials, stats } from '@/mocks/testimonials';

export default function AboutPage() {
  const brandSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '韩国漫画网',
    url: 'http://sweetbird.cn/',
    description: '韩国漫画网专注提供免费韩国漫画在线阅读，采用无遮挡下拉式阅读模式，为漫画爱好者提供舒适的在线阅读体验。',
    foundingDate: '2024',
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'customer service',
      email: 'hello@sweetbird.cn',
      availableLanguage: ['Chinese'],
    },
    sameAs: ['http://sweetbird.cn/'],
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: '韩国漫画网上的漫画都是免费的吗？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '是的，韩国漫画网上的所有漫画作品均免费提供在线阅读。我们致力于为漫画爱好者提供免费、优质的韩漫阅读服务，无需付费或订阅。',
        },
      },
      {
        '@type': 'Question',
        name: '无遮挡下拉式阅读是什么意思？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '无遮挡下拉式阅读是指漫画内容以上下滑动的方式连续展示，读者无需点击翻页按钮即可流畅阅读整部漫画。这种方式避免了传统翻页带来的打断感，让读者能够完全沉浸在漫画情节中，是韩国漫画网的核心特色。',
        },
      },
      {
        '@type': 'Question',
        name: '韩国漫画网的更新速度如何？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '韩国漫画网保持与韩国原创平台同步的更新速度。大部分热门连载漫画每周都会按时更新最新章节，确保读者能第一时间阅读到自己喜欢的作品。',
        },
      },
      {
        '@type': 'Question',
        name: '如何在手机上看韩国漫画网的漫画？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '韩国漫画网采用响应式设计，支持手机、平板和电脑等多种设备访问。您只需在手机浏览器中打开网站即可获得适配移动端的阅读体验，无需下载任何应用。',
        },
      },
    ],
  };

  return (
    <>
      <SEOHead
        title="关于我们-韩国漫画网-免费韩漫阅读平台"
        description="韩国漫画网专注提供免费韩国漫画在线阅读，无遮挡下拉式阅读体验。了解我们的品牌故事、用户口碑和联系方式，开启你的韩漫阅读之旅。"
        keywords="韩国漫画网,关于我们,免费漫画,韩漫,在线阅读,品牌介绍"
        canonicalUrl="http://sweetbird.cn/about"
      />
      <script type="application/ld+json">{JSON.stringify(brandSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>

      <Navbar />

      <main className="pt-16">
        <div className="w-full px-4 md:px-6 py-6">
          <Breadcrumb
            items={[
              { label: '首页', path: '/' },
              { label: '关于我们' },
            ]}
          />
        </div>

        {/* Hero */}
        <section className="relative overflow-hidden">
          <div className="grid grid-cols-1 md:grid-cols-2 min-h-[440px]">
            <div className="flex flex-col justify-center px-4 md:px-14 py-14 bg-background-50">
              <h1 className="font-heading text-3xl md:text-5xl lg:text-6xl font-black text-foreground-900 leading-tight">
                关于我们
                <br />
                品牌故事
              </h1>
              <p className="mt-5 text-sm md:text-base text-foreground-500 leading-relaxed max-w-md">
                韩国漫画网诞生于对韩漫的热爱。我们致力于为中国读者打造
                一个免费、流畅、高质量的韩国漫画在线阅读平台。
              </p>
            </div>
            <div className="relative min-h-[300px] md:min-h-[440px] overflow-hidden bg-background-100">
              <img
                src="https://readdy.ai/api/search-image?query=Korean%20manhwa%20creative%20studio%20workspace%2C%20warm%20lighting%2C%20drawing%20tablet%20on%20wooden%20desk%2C%20cozy%20atmosphere%2C%20art%20supplies%2C%20plants%2C%20minimalist%20interior%2C%20creative%20workspace%2C%20warm%20beige%20and%20cream%20tones%2C%20soft%20natural%20light&width=960&height=440&seq=about-hero-bg&orientation=landscape"
                alt="韩国漫画网品牌创作空间"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </section>

        {/* Stats */}
        <section className="py-14 md:py-20 bg-background-50">
          <div className="w-full px-4 md:px-6">
            <div className="max-w-4xl mx-auto">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8">
                {stats.map((stat) => (
                  <div key={stat.label} className="text-center p-6 rounded-xl bg-background-100">
                    <span className="block text-3xl md:text-4xl font-black text-primary-500 font-heading">
                      {stat.value}
                    </span>
                    <span className="block mt-2 text-sm text-foreground-500">{stat.label}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Brand Story */}
        <section id="story" className="py-14 md:py-20 bg-background-100">
          <div className="w-full px-4 md:px-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-14 max-w-5xl mx-auto items-center">
              <div>
                <span className="px-3 py-1 rounded-full text-xs font-medium bg-primary-100 text-primary-700">
                  品牌介绍
                </span>
                <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-900 mt-4 leading-tight">
                  专注体验
                  <br />
                  品质阅读
                </h2>
                <div className="mt-6 space-y-4 text-sm md:text-base text-foreground-600 leading-relaxed">
                  <p>
                    韩国漫画网成立于2024年，由一群热爱韩国漫画的创作者和技术人员共同打造。我们深知漫画读者的需求——快速加载、流畅阅读、无广告打扰。因此，我们从创立之初就确立了<strong>无遮挡下拉式阅读</strong>作为核心体验。
                  </p>
                  <p>
                    与传统翻页漫画网站不同，韩国漫画网的下拉式阅读模式让您像浏览社交动态一样自然地阅读漫画。上下滑动之间，漫画情节如行云流水般呈现，没有任何点击翻页带来的打断感。这种沉浸式体验是数百万读者选择我们的原因。
                  </p>
                  <p>
                    我们与韩国多家漫画平台建立了合作关系，确保第一时间获取最新、最热门的韩漫作品。从连载中的热门大作到经典的完结神作，韩国漫画网努力为您呈现最完整的韩漫阅读库。
                  </p>
                </div>
              </div>
              <div className="rounded-2xl overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=Korean%20creative%20team%20collaborating%20in%20modern%20office%2C%20warm%20atmosphere%2C%20designers%20working%20on%20tablets%2C%20creative%20workplace%2C%20natural%20light%20from%20large%20windows%2C%20plants%20and%20wood%20furniture%2C%20clean%20minimalist%20aesthetic%2C%20teamwork%20vibe%2C%20warm%20beige%20tones&width=640&height=480&seq=about-team&orientation=landscape"
                  alt="韩国漫画网团队协作"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Reputation */}
        <section id="reputation" className="py-14 md:py-20 bg-background-50">
          <div className="w-full px-4 md:px-6">
            <div className="max-w-4xl mx-auto">
              <div className="text-center mb-12">
                <span className="text-xs font-medium tracking-widest uppercase text-secondary-500">
                  用户口碑
                </span>
                <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-900 mt-3">
                  读者眼中的我们
                </h2>
                <p className="mt-3 text-sm text-foreground-500 max-w-lg mx-auto">
                  来自真实读者的评价，您的每一次肯定都是我们前进的动力
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {testimonials.map((t) => (
                  <div key={t.id} className="p-6 rounded-xl bg-background-100 border border-background-200/70">
                    <div className="flex items-center gap-2 mb-4">
                      <div className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-foreground-900 text-background-50">
                        <i className="ri-star-fill text-accent-500 text-xs"></i>
                        <span className="text-xs font-medium">{t.rating}</span>
                      </div>
                      <span className="text-xs text-foreground-400">{t.source}</span>
                    </div>
                    <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                      &ldquo;{t.comment}&rdquo;
                    </p>
                    <div className="flex items-center gap-2">
                      <img
                        src={t.avatarUrl}
                        alt={t.name}
                        className="w-10 h-10 rounded-full object-cover"
                      />
                      <span className="text-sm font-medium text-foreground-700">{t.name}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section id="faq" className="py-14 md:py-20 bg-background-100">
          <div className="w-full px-4 md:px-6">
            <div className="max-w-3xl mx-auto">
              <div className="text-center mb-12">
                <span className="text-xs font-medium tracking-widest uppercase text-accent-500">
                  常见问题
                </span>
                <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-900 mt-3">
                  关于韩漫阅读
                </h2>
              </div>

              <div className="space-y-4">
                {[
                  { q: '韩国漫画网上的漫画都是免费的吗？', a: '是的，韩国漫画网上的所有漫画作品均免费提供在线阅读。我们致力于为漫画爱好者提供免费、优质的韩漫阅读服务，无需付费或订阅任何会员。' },
                  { q: '无遮挡下拉式阅读是什么意思？', a: '无遮挡下拉式阅读是指漫画内容以上下滑动的方式连续展示，读者无需点击翻页按钮即可流畅阅读整部漫画。这种方式避免了传统翻页带来的打断感，让读者能够完全沉浸在漫画情节中，是韩国漫画网的核心特色阅读体验。' },
                  { q: '韩国漫画网的更新速度如何？', a: '韩国漫画网保持与韩国原创平台同步的更新速度。大部分热门连载漫画每周都会按时更新最新章节，确保读者能第一时间阅读到自己喜欢的作品，永远不会错过精彩内容。' },
                  { q: '如何在手机上舒适地阅读韩国漫画？', a: '韩国漫画网采用响应式设计，完美适配手机、平板和电脑等多种设备。您只需在手机浏览器中打开网站即可获得适配移动端的下拉式阅读体验，无需下载任何额外应用。我们建议在WiFi环境下阅读以获得更流畅的加载速度。' },
                  { q: '可以下载漫画离线阅读吗？', a: '目前韩国漫画网提供在线流式阅读服务。我们推荐您通过在线方式阅读，这样可以确保您始终获得最新更新和最佳画质体验。在线下拉式阅读也无需占用您的设备存储空间。' },
                ].map((item, index) => (
                  <details key={index} className="group bg-background-50 rounded-xl border border-background-200/70">
                    <summary className="flex items-center justify-between px-6 py-4 cursor-pointer list-none">
                      <span className="text-sm font-medium text-foreground-800 pr-4">
                        {item.q}
                      </span>
                      <span className="flex-shrink-0 w-6 h-6 rounded-full bg-background-200/70 flex items-center justify-center text-foreground-400 group-open:rotate-45 transition-transform">
                        <i className="ri-add-line text-sm"></i>
                      </span>
                    </summary>
                    <div className="px-6 pb-4 text-sm text-foreground-500 leading-relaxed">
                      {item.a}
                    </div>
                  </details>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Contact */}
        <section id="contact" className="py-14 md:py-20 bg-background-50">
          <div className="w-full px-4 md:px-6">
            <div className="max-w-3xl mx-auto">
              <div className="text-center mb-10">
                <span className="text-xs font-medium tracking-widest uppercase text-primary-500">
                  联系我们
                </span>
                <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-900 mt-3">
                  与我们取得联系
                </h2>
                <p className="mt-3 text-sm text-foreground-500 max-w-lg mx-auto">
                  有任何问题、建议或合作意向，欢迎随时联系我们
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10">
                <div className="p-6 rounded-xl bg-background-100 text-center">
                  <div className="w-12 h-12 mx-auto rounded-full bg-primary-100 flex items-center justify-center text-primary-600">
                    <i className="ri-mail-line text-xl"></i>
                  </div>
                  <h3 className="mt-4 text-sm font-semibold text-foreground-800">电子邮箱</h3>
                  <p className="mt-1 text-sm text-foreground-500">hello@sweetbird.cn</p>
                </div>
                <div className="p-6 rounded-xl bg-background-100 text-center">
                  <div className="w-12 h-12 mx-auto rounded-full bg-accent-100 flex items-center justify-center text-accent-600">
                    <i className="ri-wechat-line text-xl"></i>
                  </div>
                  <h3 className="mt-4 text-sm font-semibold text-foreground-800">微信公众号</h3>
                  <p className="mt-1 text-sm text-foreground-500">韩国漫画网</p>
                </div>
                <div className="p-6 rounded-xl bg-background-100 text-center">
                  <div className="w-12 h-12 mx-auto rounded-full bg-secondary-100 flex items-center justify-center text-secondary-600">
                    <i className="ri-time-line text-xl"></i>
                  </div>
                  <h3 className="mt-4 text-sm font-semibold text-foreground-800">响应时间</h3>
                  <p className="mt-1 text-sm text-foreground-500">24小时内回复</p>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}